import { Component, ElementRef, inject, input, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ApiService } from '../../../core/services/api.service';
import { AppNotification } from '../../../models/notification.model';
import { DevOpsTicket } from '../../../models/devops-ticket.model';
import { TicketDetailDialogComponent } from '../../../features/tickets/ticket-detail-dialog.component';
import { TaskDetailDialogComponent } from '../../../features/projects/tasks/task-detail-dialog/task-detail-dialog.component';

@Component({
  selector: 'app-notification-bell',
  standalone: true,
  imports: [MatIconModule, MatTooltipModule],
  templateUrl: './notification-bell.component.html',
  styleUrl: './notification-bell.component.scss',
})
export class NotificationBellComponent implements OnInit, OnDestroy {
  private readonly api    = inject(ApiService);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);

  collapsed = input(false);

  open          = signal(false);
  unreadCount   = signal(0);
  notifications = signal<AppNotification[]>([]);
  loading       = signal(false);
  panelTop      = signal(0);

  @ViewChild('triggerBtn') triggerBtn!: ElementRef<HTMLButtonElement>;

  private pollInterval: ReturnType<typeof setInterval> | null = null;

  ngOnInit(): void {
    this.fetchUnreadCount();
    this.pollInterval = setInterval(() => this.fetchUnreadCount(), 30_000);
  }

  ngOnDestroy(): void {
    if (this.pollInterval) clearInterval(this.pollInterval);
  }

  togglePanel(): void {
    if (this.open()) {
      this.closePanel();
    } else {
      this.openPanel();
    }
  }

  private openPanel(): void {
    // Capture button Y so panel aligns with it
    const rect = this.triggerBtn.nativeElement.getBoundingClientRect();
    this.panelTop.set(rect.top);

    this.open.set(true);
    this.loading.set(true);
    this.api.get<AppNotification[]>('/api/notifications').subscribe({
      next: (list) => { this.notifications.set(list); this.loading.set(false); },
      error: () => this.loading.set(false),
    });
  }

  private closePanel(): void {
    this.open.set(false);
    this.api.patch('/api/notifications/read-all', {}).subscribe({
      next: () => this.unreadCount.set(0),
      error: () => {},
    });
  }

  markAllRead(): void {
    this.api.patch('/api/notifications/read-all', {}).subscribe({
      next: () => {
        this.unreadCount.set(0);
        this.notifications.update((list) => list.map((n) => ({ ...n, is_read: true })));
      },
      error: () => {},
    });
  }

  private fetchUnreadCount(): void {
    this.api.get<{ count: number }>('/api/notifications/unread-count').subscribe({
      next: (r) => this.unreadCount.set(r.count),
      error: () => {},
    });
  }

  navigateTo(n: AppNotification): void {
    if (!n.is_read) {
      this.api.patch(`/api/notifications/${n.id}/read`, {}).subscribe({
        next: () => {
          this.notifications.update(list =>
            list.map(item => item.id === n.id ? { ...item, is_read: true } : item)
          );
          this.unreadCount.set(this.notifications().filter(i => !i.is_read).length);
        },
        error: () => {},
      });
    }

    this.open.set(false);

    if (n.entity_type === 'task') {
      this.api.get<{ task_id: number; project_id: number }>(`/api/tasks/${n.entity_id}`).subscribe({
        next: ({ task_id, project_id }) => {
          this.dialog.open(TaskDetailDialogComponent, {
            maxWidth: '95vw',
            data: {
              taskId:        task_id,
              projectId:     project_id,
              focusComments: n.type === 'task_comment',
            },
          });
        },
        error: () => this.router.navigate(['/projects']),
      });
    } else if (n.entity_type === 'devops_ticket') {
      this.api.get<DevOpsTicket>(`/api/tickets/${n.entity_id}`).subscribe({
        next: (ticket) => {
          this.dialog.open(TicketDetailDialogComponent, {
            width: '640px',
            data: { ticket },
          });
        },
        error: () => this.router.navigate(['/tickets']),
      });
    } else if (n.entity_type === 'project') {
      this.router.navigate(['/projects', n.entity_id]);
    } else if (n.entity_type === 'governance_week') {
      this.router.navigate(['/accounts', n.entity_id], { queryParams: { tab: 'governance' } });
    } else if (n.entity_type === 'access_request') {
      this.router.navigate(['/admin-settings']);
    } else if (n.entity_type === 'account') {
      this.router.navigate(['/accounts', n.entity_id], { queryParams: { tab: 'resources' } });
    } else if (n.entity_type === 'resource_link') {
      this.api.get<{ project_id: number }>(`/api/resource-links/item/${n.entity_id}`).subscribe({
        next: (link) => this.router.navigate(['/projects', link.project_id], {
          queryParams: { tab: 'resources', highlight: n.entity_id },
        }),
        error: () => {},
      });
    } else if (n.entity_type === 'account_resource_link') {
      this.api.get<{ account_id: number }>(`/api/account-resource-links/item/${n.entity_id}`).subscribe({
        next: (link) => this.router.navigate(['/accounts', link.account_id], {
          queryParams: { tab: 'resources', highlight: n.entity_id },
        }),
        error: () => {},
      });
    }
  }

  timeAgo(iso: string): string {
    const utc = iso.endsWith('Z') || iso.includes('+') ? iso : iso + 'Z';
    const diff = Date.now() - new Date(utc).getTime();
    const mins = Math.floor(diff / 60_000);
    if (mins < 1)  return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24)  return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  }
}
